/**
 * useIsMounted — guard de montaje uniforme (SSCA-FIX-0)
 *
 * Devuelve una función estable que indica si el componente sigue montado.
 * Es el patrón canónico para inhibir `setState` en la continuación de una
 * operación async: cuando una promesa se settlea tras el desmontaje del
 * componente, el `setState` de la continuación agenda trabajo en el scheduler
 * de React sobre un componente ya muerto; en CI con jsdom esa tarea puede leer
 * un `window` ya destruido por el teardown → `ReferenceError` → unhandled
 * rejection → CI rojo flaky (ver `OBS-DEBT-SETSTATE-POST-AWAIT-UNGUARDED-CODEBASE`).
 *
 * Uso:
 *   const isMounted = useIsMounted();
 *   useEffect(() => {
 *     buscarCandidatas().then(r => { if (isMounted()) setCandidatas(r); });
 *   }, [isMounted]);
 *
 * Unifica en un hook reutilizable el guard manual `_isMountedRef` de
 * `Step2.tsx` (sesión `S2WG`), plantilla canónica del mini-arco de fix `SSCA`.
 * La función devuelta tiene identidad estable (`useCallback` sin dependencias):
 * es segura en arrays de dependencias y se puede pasar a componentes hijos sin
 * provocar renders adicionales.
 *
 * @returns función `() => boolean` — `true` si el componente está montado.
 */
import { useCallback, useEffect, useRef } from 'react';

export function useIsMounted(): () => boolean {
  const mountedRef = useRef(true);

  useEffect(() => {
    // Re-afirmar en el montaje cubre el remontaje: StrictMode en desarrollo
    // ejecuta montar → desmontar → montar; la limpieza dejaría `false` y este
    // cuerpo lo restaura a `true` en el segundo montaje.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => mountedRef.current, []);
}

export default useIsMounted;
